// auth.interceptor.ts - Enhanced implementation for HttpOnly cookies
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, timer, Subject } from 'rxjs';
import { catchError, switchMap, filter, take, finalize, takeUntil } from 'rxjs/operators';
import { AuthService } from '@features/auth/services/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private refreshFailed$ = new Subject<void>();

  // Public pages that should not trigger redirect on 401
  private publicPaths = ['/', '/pricing', '/about', '/help', '/faq', '/privacy', '/terms', '/cookies', '/security', '/documentation', '/auth/login', '/auth/register', '/auth/demo', '/login', '/register'];

  constructor(private authService: AuthService, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip adding credentials for external APIs (like Geoapify)
    // Only add credentials for our internal API
    const isExternalApi = !req.url.includes('/api/') &&
                          (req.url.startsWith('http://') || req.url.startsWith('https://'));

    // For HttpOnly cookies, we need withCredentials: true on internal API requests only
    const authReq = isExternalApi
      ? req  // Don't modify external API requests
      : req.clone({ withCredentials: true });  // Add credentials for internal API

    console.log('[AuthInterceptor] Request:', req.method, req.url, 'External:', isExternalApi);

    // Skip interceptor handling for the refresh token endpoint itself
    if (req.url.includes('/auth/refresh-token')) {
      console.log('[AuthInterceptor] Skipping refresh-token endpoint');
      return next.handle(authReq);
    }

    return next.handle(authReq).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          console.error('[AuthInterceptor] 401 Unauthorized on:', req.url);
          console.error('[AuthInterceptor] Error details:', error);

          // Check if current page is a public page - don't redirect
          const currentPath = this.router.url.split('?')[0];
          const isPublicPage = this.publicPaths.some(p => currentPath.startsWith(p));

          console.log('[AuthInterceptor] Debug - Method:', req.method, 'isPublicPage:', isPublicPage, 'currentPath:', currentPath);

          if (isPublicPage) {
            // On public pages, differentiate between read and write operations:
            // - GET requests (reading data) → fail silently, don't logout
            // - POST/PUT/DELETE (actions like checkout) → try refresh, redirect if needed
            if (req.method === 'GET') {
              console.log('[AuthInterceptor] On public page (GET), not redirecting or clearing auth on 401');
              return throwError(() => error);
            }
            // For write operations on public pages, still try to refresh token
            console.log('[AuthInterceptor] On public page but write operation (' + req.method + '), attempting refresh');
          }

          // Only attempt refresh if this isn't already an auth endpoint (except status check)
          if (req.url.includes('/auth/') && !req.url.includes('/auth/status')) {
            console.log('[AuthInterceptor] Auth endpoint failed, not attempting refresh');
            return throwError(() => error);
          }

          // Don't attempt refresh if user is not authenticated at all
          if (!this.authService.isAuthenticated()) {
            console.log('[AuthInterceptor] User not authenticated, skipping token refresh');
            return throwError(() => error);
          }

          console.log('[AuthInterceptor] Attempting token refresh...');
          return this.handle401Error(authReq, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      console.log('[AuthInterceptor] Starting token refresh (not already refreshing)');
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap(user => {
          console.log('[AuthInterceptor] Token refresh successful, waiting for cookie to be set...');
          this.refreshTokenSubject.next(user);
          // Add a small delay to allow browser to process the Set-Cookie header
          // This is necessary because HttpOnly cookies are set asynchronously
          return timer(100).pipe(
            switchMap(() => {
              console.log('[AuthInterceptor] Retrying request to:', request.url);
              // Re-clone request with credentials to ensure new cookie is sent
              const retryRequest = request.clone({ withCredentials: true });
              return next.handle(retryRequest);
            })
          );
        }),
        catchError((error: HttpErrorResponse) => {
          // Check if this is the refresh failing or the retry failing
          if (error.url?.includes('/auth/refresh-token')) {
            console.error('[AuthInterceptor] Token refresh FAILED:', { status: error.status, message: error.message });
          } else {
            console.error('[AuthInterceptor] Retry request FAILED after refresh:', { status: error.status, message: error.message });
          }

          // Only clear auth state and redirect on an explicit 401.
          // Network errors, 500s, etc. are transient — don't log the user out.
          if (error.status !== 401) {
            console.warn('[AuthInterceptor] Transient error — preserving auth state, not redirecting');
            this.refreshFailed$.next();
            return throwError(() => error);
          }

          // Check if current page is a public page - don't redirect
          const currentPath = this.router.url.split('?')[0];
          const isPublicPage = this.publicPaths.some(p => currentPath.startsWith(p));

          if (isPublicPage) {
            console.log('[AuthInterceptor] On public page, not redirecting to login');
            this.authService.clearAuthState();
            return throwError(() => error);
          }

          console.error('[AuthInterceptor] Refresh token rejected (401) → redirecting to login');

          // Force user to login page
          this.authService.clearAuthState();
          this.router.navigate(['/login'], {
            queryParams: {
              session: 'expired',
              returnUrl: this.router.url
            }
          });

          // Unblock any requests waiting on refreshTokenSubject
          this.refreshFailed$.next();

          return throwError(() => error);
        }),
        finalize(() => {
          console.log('[AuthInterceptor] Token refresh attempt finished');
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
        })
      );
    } else {
      console.log('[AuthInterceptor] Token refresh already in progress, waiting...');
      // Wait for the ongoing refresh to complete (or fail)
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        takeUntil(this.refreshFailed$),
        switchMap(() => {
          console.log('[AuthInterceptor] Token refresh completed, waiting for cookie...');
          // Add delay for cookie processing
          return timer(100).pipe(
            switchMap(() => {
              console.log('[AuthInterceptor] Retrying request after waiting');
              // Re-clone request with credentials
              const retryRequest = request.clone({ withCredentials: true });
              return next.handle(retryRequest);
            })
          );
        }),
        catchError(() => throwError(() => new Error('Token refresh failed')))
      );
    }
  }
}
